// A tab still running the previous build asks for hashed chunks the new deploy no
// longer has. Reloading picks up the new HTML and its chunks.
//
// Deliberately not hooked to `vite:preloadError`: that also fires for background
// prefetches (Header warms routes on hover and after every navigation), which would
// reload the page under a user who never opened the failing route.

const STORAGE_KEY = 'staleChunkReloadAt'
// reload() starts the next navigation at once, so a page whose timeOrigin is this close
// to the stored timestamp is the one our reload produced.
const RELOAD_NAVIGATION_WINDOW_MS = 10_000
// A page our reload produced may still recover from a later deploy, just not this soon.
const LOOP_GUARD_MS = 5 * 60_000

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite
  /Failed to load script resources/i, // Module Federation RUNTIME-008 (remoteEntry)
]

let reloadInProgress = false

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message))
}

const readLastReloadAt = (): number => {
  try {
    return Number(sessionStorage.getItem(STORAGE_KEY)) || 0
  } catch {
    return 0
  }
}

// Measured from navigation start rather than from the failure, so a reloaded page that
// is slow to load (the new build failing too) can't slip past the guard and loop.
const isPageFromOurReload = (lastReloadAt: number, now: number): boolean =>
  lastReloadAt > 0 &&
  Math.abs(performance.timeOrigin - lastReloadAt) < RELOAD_NAVIGATION_WINDOW_MS &&
  now - lastReloadAt < LOOP_GUARD_MS

/**
 * Reloads the page unless this page is itself the result of such a reload. Returns
 * whether a reload is under way — including one an earlier failure already started.
 */
export function reloadForStaleChunk(now = Date.now()): boolean {
  if (typeof window === 'undefined') return false
  if (reloadInProgress) return true
  if (isPageFromOurReload(readLastReloadAt(), now)) return false
  try {
    sessionStorage.setItem(STORAGE_KEY, String(now))
  } catch {
    // Without storage there is no loop guard, so don't risk reloading.
    return false
  }
  reloadInProgress = true
  window.location.reload()
  return true
}
